import { IComparablesTransactionDTO } from "react-migration/lib/typings/comparables/Comparables";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { userStore } from "src/js/stores/user/store";
import { useComparableByTransactionId } from "react-migration/domains/comparables/hooks/useComparableByTransactionId";
import { LayerTypeSelectionDetailViewProps } from "../../types";
import { OtherComparablesSection } from "./OtherComparablesSection";
import { useSnapshot } from "valtio";
import { ComparableItemDetail } from "react-migration/domains/comparables/components/Results/ComparableItemDetail";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { useZooplaComparableByUPRNv2 } from "react-migration/domains/comparables/hooks/useZooplaComparableByUPRNv2";
import { useZooplaListingsByUPRN } from "react-migration/domains/comparables/hooks/useZooplaListingsByUPRN";
import { ZooplaComparable } from "react-migration/lib/typings/Zoopla";
import { useFeatureFlag } from "react-migration/lib/user/useFeatureFlag";

function ComparablesSelectionDetailViewInner({
  record,
  recordLoading,
}: Readonly<{
  record: IComparablesTransactionDTO;
  recordLoading: boolean;
}>) {
  const { user, locale } = useSnapshot(userStore);
  const unitPreference = user.settings.unit;
  const usAccess = useFeatureFlag(Feature.usAccess);
  const hasHometrack = hasFeature(Feature.zooplaFullAccess) && !usAccess;

  const { data: zooplaData, loading: zooplaDataLoading } = useZooplaComparableByUPRNv2({
    uprn: record.uprn,
    skip: !hasHometrack,
  });
  const { data: listingsData, loading: listingsLoading } = useZooplaListingsByUPRN({
    uprn: record.uprn,
    skip: !hasHometrack,
  });

  return (
    <ComparableItemDetail
      record={record}
      recordLoading={recordLoading}
      zooplaData={zooplaData as ZooplaComparable}
      zooplaDataLoading={zooplaDataLoading}
      unitPreference={unitPreference}
      listingsData={listingsData}
      listingsLoading={listingsLoading}
      locale={locale}
      isZoopla={false}
    />
  );
}

export function LandTechComparableSelectionDetailView({
  selection,
  setDetailSelection,
}: Readonly<LayerTypeSelectionDetailViewProps>) {
  const { loading, data } = useComparableByTransactionId({
    transactionId: selection.id!,
  });

  if (loading) {
    return (
      <div className="relative h-[200px]">
        <SkeletonLoading rows={8} />
      </div>
    );
  }

  if (!data) {
    return <div className="flex flex-col gap-y-2 bg-white">Could not find record</div>;
  }

  return (
    <div className="flex flex-col gap-y-2 bg-white">
      <ComparablesSelectionDetailViewInner record={data} recordLoading={loading} />

      <OtherComparablesSection selection={selection} onSelection={setDetailSelection} />
    </div>
  );
}
